import {
  CartErrorEvent,
  CartLinesUpdateEvent,
} from '@shopify/events';

class ZsCartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('submit', this.#handleSubmit);

    this.#track?.addEventListener(
      'scroll',
      this.#handleTrackScroll,
      {
        passive: true,
      }
    );

    window.addEventListener(
      'resize',
      this.#handleWindowResize
    );

    requestAnimationFrame(() => {
      this.#updateCarouselControls();
    });
  }

  disconnectedCallback() {
    this.removeEventListener(
      'click',
      this.#handleClick
    );

    this.removeEventListener(
      'submit',
      this.#handleSubmit
    );

    this.#track?.removeEventListener(
      'scroll',
      this.#handleTrackScroll
    );

    window.removeEventListener(
      'resize',
      this.#handleWindowResize
    );
  }

  get #track() {
    return this.querySelector(
      '[data-zs-recs-track]'
    );
  }

  get #previousButton() {
    return this.querySelector(
      '[data-zs-recs-previous]'
    );
  }

  get #nextButton() {
    return this.querySelector(
      '[data-zs-recs-next]'
    );
  }

  #handleTrackScroll = () => {
    this.#updateCarouselControls();
  };

  #handleWindowResize = () => {
    this.#updateCarouselControls();
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const previousButton = event.target.closest(
      '[data-zs-recs-previous]'
    );

    if (previousButton) {
      event.preventDefault();
      this.#scrollCarousel(-1);
      return;
    }

    const nextButton = event.target.closest(
      '[data-zs-recs-next]'
    );

    if (nextButton) {
      event.preventDefault();
      this.#scrollCarousel(1);
    }
  };

  #handleSubmit = async (event) => {
    const form =
      event.target instanceof HTMLFormElement
        ? event.target.closest(
            '[data-zs-recommendation-form]'
          )
        : null;

    if (!form) return;

    event.preventDefault();

    const button = form.querySelector(
      '[data-zs-recommendation-add]'
    );

    const buttonText = form.querySelector(
      '[data-zs-recommendation-button-text]'
    );

    const errorElement = form.querySelector(
      '[data-zs-recommendation-error]'
    );

    const variantInput = form.querySelector(
      'input[name="id"]'
    );

    const quantityInput = form.querySelector(
      'input[name="quantity"]'
    );

    if (
      !(button instanceof HTMLButtonElement) ||
      !(variantInput instanceof HTMLInputElement)
    ) {
      return;
    }

    if (button.disabled) return;

    const variantId = variantInput.value;
    const quantity = Math.max(
      1,
      Number(quantityInput?.value || 1)
    );

    const defaultLabel =
      button.dataset.defaultLabel || 'Add';

    const loadingLabel =
      button.dataset.loadingLabel || 'Adding…';

    const addedLabel =
      button.dataset.addedLabel || 'Added';

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.remove('is-added');

    if (buttonText) {
      buttonText.textContent = loadingLabel;
    }

    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const sectionIds = new Set([
      'cart-drawer-section',
    ]);

    cartItemsComponents.forEach((component) => {
      if (
        component instanceof HTMLElement &&
        component.dataset.sectionId
      ) {
        sectionIds.add(
          component.dataset.sectionId
        );
      }
    });

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [
          {
            merchandiseId: variantId,
            quantity,
          },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const rootUrl =
        window.Shopify?.routes?.root || '/';

      const addResponse = await fetch(
        `${rootUrl}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity,
              },
            ],
            sections: Array.from(sectionIds).join(
              ','
            ),
            sections_url:
              window.location.pathname,
          }),
        }
      );

      const addResult = await addResponse.json();

      if (
        !addResponse.ok ||
        addResult.status
      ) {
        const message =
          addResult.description ||
          addResult.message ||
          'This product could not be added.';

        throw new Error(message);
      }

      const cartResponse = await fetch(
        `${rootUrl}cart.js`,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!cartResponse.ok) {
        throw new Error(
          'The cart could not be refreshed.'
        );
      }

      const ajaxCart = await cartResponse.json();

      button.classList.add('is-added');

      if (buttonText) {
        buttonText.textContent = addedLabel;
      }

      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            ajaxCart
          ),
        detail: {
          items: ajaxCart.items,
          itemCount: quantity,
          productId: form.dataset.productId,
          sections: addResult.sections,
          source:
            'zs-cart-drawer-recommendations',
          sourceId:
            form.dataset.productId || variantId,
          didError: false,
        },
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'This product could not be added.';

      deferredEventPromise.reject(
        error instanceof Error
          ? error
          : new Error(message)
      );

      form.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
        })
      );

      button.disabled = false;
      button.removeAttribute('aria-busy');

      if (buttonText) {
        buttonText.textContent = defaultLabel;
      }

      if (errorElement) {
        errorElement.textContent = message;
        errorElement.hidden = false;
      }
    }
  };

  #scrollCarousel(direction) {
    const track = this.#track;

    if (!track) return;

    const firstCard = track.querySelector(
      '.zs-drawer-rec-card'
    );

    const cardWidth =
      firstCard instanceof HTMLElement
        ? firstCard.getBoundingClientRect().width
        : track.clientWidth * 0.5;

    const computedStyles =
      window.getComputedStyle(track);

    const gap =
      Number.parseFloat(
        computedStyles.columnGap ||
          computedStyles.gap ||
          '0'
      ) || 0;

    track.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: 'smooth',
    });
  }

  #updateCarouselControls() {
    const track = this.#track;
    const previousButton = this.#previousButton;
    const nextButton = this.#nextButton;

    if (
      !track ||
      !previousButton ||
      !nextButton
    ) {
      return;
    }

    const maximumScroll =
      track.scrollWidth - track.clientWidth;

    previousButton.disabled =
      track.scrollLeft <= 2;

    nextButton.disabled =
      track.scrollLeft >= maximumScroll - 2;
  }
}

if (
  !customElements.get(
    'zs-cart-drawer-recommendations'
  )
) {
  customElements.define(
    'zs-cart-drawer-recommendations',
    ZsCartDrawerRecommendations
  );
}